import {
  QUEUE_HEAD_STALL_THRESHOLD_MS,
  type UploadSignals,
} from "./uploadSignals"

/**
 * Combines what every upload path reports into one per-project upload status
 * (issue #209). It uploads nothing itself: row changes belong to PowerSync's
 * CRUD loop (`ps_crud`, which also carries tracks), photo files to the
 * attachment queue, and parked failures to `sync_failures`. Watches over
 * those (see uploadMonitorWatches.ts) and the connector's own signals
 * (uploadSignals.ts) feed it; React reads it with `useSyncExternalStore`.
 *
 * Kept free of database and React imports so the rules can be unit tested
 * with plain inputs.
 */

/** Queued row changes for one project, in the order they'll upload. */
export type QueueGroup = {
  /** null for org-level changes (species) and anything unattributable. */
  projectId: string | null
  rows: number
  tracks: number
  /**
   * Changes for records the user has deleted (see QUEUED_OPS_CTE). They
   * still have to upload, but the record is already gone from the user's
   * point of view, so they're left out of the project's status and counts.
   */
  deletions: number
  /** `ps_crud` id of this project's oldest queued change. */
  firstClientId: number
  /** How many queued changes are ahead of `firstClientId`. */
  ahead: number
}

/** Photo files waiting to upload for one project. */
export type PhotoGroup = {
  projectId: string | null
  photos: number
  bytes: number
}

/**
 * Items waiting to upload for one project, as a surveyor counts them: an
 * observation, a site, a map record or a track, however many rows and photo
 * files it takes. See ITEMS_BY_PROJECT_SQL.
 */
export type ItemGroup = {
  projectId: string | null
  items: number
}

/** Things that need the user's attention for one project. */
export type FailureGroup = {
  projectId: string | null
  failures: number
}

export type UploadPhase =
  /** Nothing queued, and nothing uploaded since the app started. */
  | "idle"
  /** Queued, but can't move: offline, or behind other projects' changes. */
  | "waiting"
  | "uploading"
  /** The same upload has kept failing for QUEUE_HEAD_STALL_THRESHOLD_MS. */
  | "stalled"
  /** Everything queued in this session has gone. */
  | "done"

export type UploadCounter = { remaining: number; sessionTotal: number }

export type ProjectUploadSnapshot = {
  /**
   * False until the queue, photo, item and failure watches have each
   * reported once.
   * Until then the counts are unknown, not zero: say nothing reassuring.
   */
  loaded: boolean
  phase: UploadPhase
  rows: UploadCounter
  photos: UploadCounter & { bytesRemaining: number }
  tracks: UploadCounter
  /**
   * Top-level items waiting (an observation with a photo is one, whatever
   * its rows and files). This is the count to show people; `rows`, `photos`
   * and `tracks` drive the upload mechanics.
   */
  items: UploadCounter
  /** sync_failures rows and permanently failed photos for this project. */
  failures: number
  /** Org-level failures (species) that belong to no project. */
  otherFailures: number
  /** Other projects' changes that will upload before this project's first. */
  aheadInQueue: number
  lastSuccessAt?: number
  blockedBy?: "offline" | "no_progress"
  /** When stalled: the stuck change belongs to a different project. */
  stalledOnOtherProject: boolean
  /** Big enough to show the progress bar rather than just the chip. */
  isLarge: boolean
  /** What the session that just finished uploaded, while phase is "done". */
  completed?: { items: number }
}

export type GlobalUploadSnapshot = {
  /** See ProjectUploadSnapshot.loaded. */
  loaded: boolean
  rows: number
  tracks: number
  photos: number
  /** Top-level items waiting, across all projects. */
  items: number
  /** Queued changes for deleted records, across all projects. */
  deletions: number
  failures: number
  /** Anything queued at all, deleted records' changes included. */
  hasPending: boolean
  isOnline: boolean
  blockedBy?: "offline" | "no_progress"
  /**
   * Hold the screen awake: a large upload is actually moving (online, not
   * stalled) somewhere in the queue.
   */
  shouldKeepAwake: boolean
  /**
   * The row queue has stalled and only deleted records' changes are left in
   * it, so no project shows anything waiting: the user can't see it.
   */
  stalledOnDeletion: boolean
}

export type UploadCompletion = {
  projectId: string
  /** Top-level items uploaded in the session that just finished. */
  items: number
  wasLarge: boolean
  /**
   * This project's failures once the queue settled. A parked change leaves
   * the queue too, so an emptied queue doesn't mean everything uploaded.
   */
  failures: number
}

/**
 * How long a project's queue must stay empty before its completion is
 * announced. Parking a change writes its sync_failures row and then removes
 * it from ps_crud, and the two watches report separately (each throttled).
 * After this, the failure count is read afresh (see setFailureReader) before
 * anything is said, so elapsed time alone never decides "all uploaded".
 */
export const COMPLETION_SETTLE_MS = 1500

type FailureReader = () => Promise<FailureGroup[]>

/**
 * Switch from the chip to the progress bar above this many queued changes in
 * one upload session…
 */
export const LARGE_UPLOAD_MIN_CHANGES = 50
/** …or at this many photos, which take most of the time on a poor link. */
export const LARGE_UPLOAD_MIN_PHOTOS = 5

type Listener = () => void
type CompletionListener = (completion: UploadCompletion) => void

type SessionTotals = {
  rows: UploadCounter
  tracks: UploadCounter
  photos: UploadCounter
  /**
   * Follows the other counters but never decides whether a session is over:
   * it comes from its own watch, which can land a moment after theirs.
   */
  items: UploadCounter
  /**
   * Deleted records' changes. Counted only in the all-projects totals: they
   * keep the app-wide session (and its keep-awake) going until the whole
   * queue drains, but never show in a project's status.
   */
  deletions: UploadCounter
}

type Clock = {
  now: () => number
  setTimer: (callback: () => void, ms: number) => unknown
  clearTimer: (handle: unknown) => void
}

const NO_PROJECT = "\u0000none"
const ALL_PROJECTS = "\u0000all"

const keyOf = (projectId: string | null) => projectId ?? NO_PROJECT

const emptyTotals = (): SessionTotals => ({
  rows: { remaining: 0, sessionTotal: 0 },
  tracks: { remaining: 0, sessionTotal: 0 },
  photos: { remaining: 0, sessionTotal: 0 },
  items: { remaining: 0, sessionTotal: 0 },
  deletions: { remaining: 0, sessionTotal: 0 },
})

/**
 * Advance a counter to a new remaining count. The session total grows by
 * every increase in what's remaining, so records added mid-upload grow the
 * denominator instead of making progress jump backwards.
 */
function advance(counter: UploadCounter, remaining: number): UploadCounter {
  const increase = Math.max(0, remaining - counter.remaining)
  return { remaining, sessionTotal: counter.sessionTotal + increase }
}

function isLargeSession(totals: SessionTotals): boolean {
  return (
    totals.rows.sessionTotal +
      totals.tracks.sessionTotal +
      totals.deletions.sessionTotal >
      LARGE_UPLOAD_MIN_CHANGES ||
    totals.photos.sessionTotal >= LARGE_UPLOAD_MIN_PHOTOS
  )
}

/** What is left to move, by the mechanics (items are a view of the same). */
function remainingOf(totals: SessionTotals): number {
  return (
    totals.rows.remaining +
    totals.tracks.remaining +
    totals.photos.remaining +
    totals.deletions.remaining
  )
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true
  if (
    typeof a !== "object" ||
    typeof b !== "object" ||
    a === null ||
    b === null
  ) {
    return false
  }
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) return false
  return aKeys.every((key) =>
    deepEqual(
      (a as Record<string, unknown>)[key],
      (b as Record<string, unknown>)[key],
    ),
  )
}

const defaultClock: Clock = {
  now: () => Date.now(),
  setTimer: (callback, ms) => setTimeout(callback, ms),
  clearTimer: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
}

export class UploadMonitor {
  private queue: QueueGroup[] = []
  private photos: PhotoGroup[] = []
  private items: ItemGroup[] = []
  private failures: FailureGroup[] = []
  private signals: UploadSignals = { rowsWaitingForConnection: false }
  private online = true
  private queueLoaded = false
  private photosLoaded = false
  private itemsLoaded = false
  private failuresLoaded = false

  private totals = new Map<string, SessionTotals>()
  private completed = new Map<string, { items: number }>()

  private version = 0
  private projectCache = new Map<
    string,
    { version: number; snapshot: ProjectUploadSnapshot }
  >()
  private globalCache: {
    version: number
    snapshot: GlobalUploadSnapshot
  } | null = null

  private listeners = new Set<Listener>()
  private completionListeners = new Set<CompletionListener>()
  private stallTimer: unknown = null
  private completionTimers = new Map<string, unknown>()
  /** Settled completions held until the failure count is known. */
  private awaitingFailures = new Map<
    string,
    Omit<UploadCompletion, "failures">
  >()
  private failureReader: FailureReader | null = null
  private readonly clock: Clock

  constructor(clock: Partial<Clock> = {}) {
    this.clock = { ...defaultClock, ...clock }
  }

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  /** Called once each time a project's queue empties after an upload. */
  onProjectCompleted(listener: CompletionListener): () => void {
    this.completionListeners.add(listener)
    return () => {
      this.completionListeners.delete(listener)
    }
  }

  setQueue(groups: QueueGroup[]): void {
    this.queue = groups
    this.queueLoaded = true
    this.recount()
  }

  setPhotos(groups: PhotoGroup[]): void {
    this.photos = groups
    this.photosLoaded = true
    this.recount()
  }

  setItems(groups: ItemGroup[]): void {
    this.items = groups
    this.itemsLoaded = true
    this.recount()
  }

  setFailures(groups: FailureGroup[]): void {
    if (this.failuresLoaded && deepEqual(this.failures, groups)) return
    this.failures = groups
    this.failuresLoaded = true
    this.changed()
    const awaiting = [...this.awaitingFailures.values()]
    this.awaitingFailures.clear()
    for (const completion of awaiting) this.emitCompletion(completion)
  }

  /**
   * How to read the failure counts afresh, used once a completion settles:
   * the failure watch may simply not have delivered yet.
   */
  setFailureReader(reader: FailureReader | null): void {
    this.failureReader = reader
  }

  private isLoaded(): boolean {
    return (
      this.queueLoaded &&
      this.photosLoaded &&
      this.itemsLoaded &&
      this.failuresLoaded
    )
  }

  setSignals(signals: UploadSignals): void {
    if (deepEqual(this.signals, signals)) return
    this.signals = signals
    this.scheduleStallCheck()
    this.changed()
  }

  setOnline(online: boolean): void {
    if (this.online === online) return
    this.online = online
    this.changed()
  }

  getProjectSnapshot(projectId: string): ProjectUploadSnapshot {
    const cached = this.projectCache.get(projectId)
    if (cached?.version === this.version) return cached.snapshot
    const next = this.computeProject(projectId)
    if (cached && deepEqual(cached.snapshot, next)) {
      cached.version = this.version
      return cached.snapshot
    }
    this.projectCache.set(projectId, { version: this.version, snapshot: next })
    return next
  }

  getGlobalSnapshot(): GlobalUploadSnapshot {
    const cached = this.globalCache
    if (cached?.version === this.version) return cached.snapshot
    const next = this.computeGlobal()
    if (cached && deepEqual(cached.snapshot, next)) {
      cached.version = this.version
      return cached.snapshot
    }
    this.globalCache = { version: this.version, snapshot: next }
    return next
  }

  /** Stop the stall timer (tests and teardown). */
  dispose(): void {
    if (this.stallTimer !== null) this.clock.clearTimer(this.stallTimer)
    this.stallTimer = null
    for (const timer of this.completionTimers.values()) {
      this.clock.clearTimer(timer)
    }
    this.completionTimers.clear()
    this.awaitingFailures.clear()
  }

  private changed() {
    this.version += 1
    for (const listener of this.listeners) listener()
  }

  /**
   * Move every project's session totals to the latest counts. Runs on each
   * input change rather than when a snapshot is read, so no intermediate
   * count is missed by the high-water marks.
   */
  private recount() {
    const remaining = new Map<string, SessionTotals>()
    const all = emptyTotals()
    const at = (key: string) => {
      let totals = remaining.get(key)
      if (!totals) {
        totals = emptyTotals()
        remaining.set(key, totals)
      }
      return totals
    }
    for (const group of this.queue) {
      const totals = at(keyOf(group.projectId))
      totals.rows.remaining += group.rows
      totals.tracks.remaining += group.tracks
      all.rows.remaining += group.rows
      all.tracks.remaining += group.tracks
      all.deletions.remaining += group.deletions
    }
    for (const group of this.photos) {
      at(keyOf(group.projectId)).photos.remaining += group.photos
      all.photos.remaining += group.photos
    }
    for (const group of this.items) {
      at(keyOf(group.projectId)).items.remaining += group.items
      all.items.remaining += group.items
    }
    remaining.set(ALL_PROJECTS, all)

    const completions: Omit<UploadCompletion, "failures">[] = []
    const keys = new Set([...this.totals.keys(), ...remaining.keys()])
    for (const key of keys) {
      const previous = this.totals.get(key) ?? emptyTotals()
      const counts = remaining.get(key) ?? emptyTotals()
      const next: SessionTotals = {
        rows: advance(previous.rows, counts.rows.remaining),
        tracks: advance(previous.tracks, counts.tracks.remaining),
        photos: advance(previous.photos, counts.photos.remaining),
        items: advance(previous.items, counts.items.remaining),
        deletions: advance(previous.deletions, counts.deletions.remaining),
      }

      if (remainingOf(next) > 0) {
        this.completed.delete(key)
        this.cancelCompletion(key)
        this.totals.set(key, next)
        continue
      }

      // The queue for this key emptied: the session ends here, and the next
      // change starts a new one from zero.
      this.totals.delete(key)
      const changes = next.rows.sessionTotal + next.tracks.sessionTotal
      // Nothing visible was queued (only deleted records' changes, or
      // nothing at all): there's no upload to announce.
      if (changes + next.photos.sessionTotal === 0) continue
      // Something was queued, so at least one item was uploaded, even if
      // the item watch hadn't reported it by the time the queue emptied.
      const items = Math.max(next.items.sessionTotal, 1)
      this.completed.set(key, { items })
      if (key !== NO_PROJECT && key !== ALL_PROJECTS) {
        completions.push({
          projectId: key,
          items,
          wasLarge: isLargeSession(next),
        })
      }
    }

    this.scheduleStallCheck()
    this.changed()
    for (const completion of completions) this.announceCompletion(completion)
  }

  private cancelCompletion(key: string) {
    this.awaitingFailures.delete(key)
    const timer = this.completionTimers.get(key)
    if (timer === undefined) return
    this.clock.clearTimer(timer)
    this.completionTimers.delete(key)
  }

  /** Announce once the queue has stayed empty and failures are known. */
  private announceCompletion(completion: Omit<UploadCompletion, "failures">) {
    const key = completion.projectId
    this.cancelCompletion(key)
    this.completionTimers.set(
      key,
      this.clock.setTimer(() => {
        this.completionTimers.delete(key)
        void this.settleCompletion(completion)
      }, COMPLETION_SETTLE_MS),
    )
  }

  private async settleCompletion(
    completion: Omit<UploadCompletion, "failures">,
  ) {
    if (this.failureReader) {
      try {
        this.setFailures(await this.failureReader())
      } catch (error) {
        console.warn("[UploadMonitor] Couldn't re-read failures:", error)
      }
    }
    // New items arrived meanwhile: this session isn't over after all.
    if (!this.completed.has(completion.projectId)) return
    if (!this.failuresLoaded) {
      this.awaitingFailures.set(completion.projectId, completion)
      return
    }
    this.emitCompletion(completion)
  }

  private emitCompletion(completion: Omit<UploadCompletion, "failures">) {
    if (!this.completed.has(completion.projectId)) return
    const failures =
      this.failures.find((group) => group.projectId === completion.projectId)
        ?.failures ?? 0
    for (const listener of this.completionListeners) {
      listener({ ...completion, failures })
    }
  }

  private totalsFor(key: string): SessionTotals {
    return this.totals.get(key) ?? emptyTotals()
  }

  private isPastThreshold(since: number | undefined): boolean {
    return (
      since !== undefined &&
      this.clock.now() - since >= QUEUE_HEAD_STALL_THRESHOLD_MS
    )
  }

  private stallState() {
    const all = this.totalsFor(ALL_PROJECTS)
    return {
      rows:
        all.rows.remaining + all.tracks.remaining + all.deletions.remaining >
          0 && this.isPastThreshold(this.signals.rowsFailingSince),
      photos:
        all.photos.remaining > 0 &&
        this.isPastThreshold(this.signals.photosFailingSince),
    }
  }

  /**
   * Whether uploads with this much queued can't start for want of a
   * connection. The connector's "waiting for connection" is about rows only:
   * photo files go through their own queue, so a project with only photos
   * left isn't held up by it, and one with both is still moving.
   */
  private isWaitingForConnection(rows: number, photos: number): boolean {
    if (!this.online) return true
    return this.signals.rowsWaitingForConnection && rows > 0 && photos === 0
  }

  /** The project whose change is at the head of the row queue. */
  private headProjectId(): string | null | undefined {
    let head: QueueGroup | undefined
    for (const group of this.queue) {
      if (!head || group.firstClientId < head.firstClientId) head = group
    }
    return head?.projectId
  }

  /**
   * Re-check once a failing upload crosses the stall threshold: nothing
   * else changes at that moment, so no input would trigger a recompute.
   */
  private scheduleStallCheck() {
    if (this.stallTimer !== null) {
      this.clock.clearTimer(this.stallTimer)
      this.stallTimer = null
    }
    const now = this.clock.now()
    const pending = [
      this.signals.rowsFailingSince,
      this.signals.photosFailingSince,
    ]
      .filter((since): since is number => since !== undefined)
      .map((since) => since + QUEUE_HEAD_STALL_THRESHOLD_MS - now)
      .filter((ms) => ms > 0)
    if (pending.length === 0) return
    this.stallTimer = this.clock.setTimer(
      () => {
        this.stallTimer = null
        this.changed()
        this.scheduleStallCheck()
      },
      Math.min(...pending),
    )
  }

  private computeProject(projectId: string): ProjectUploadSnapshot {
    const totals = this.totalsFor(projectId)
    const queueGroup = this.queue.find((group) => group.projectId === projectId)
    const photoGroup = this.photos.find(
      (group) => group.projectId === projectId,
    )
    const failures =
      this.failures.find((group) => group.projectId === projectId)?.failures ??
      0
    const otherFailures =
      this.failures.find((group) => group.projectId === null)?.failures ?? 0

    const rowsRemaining = totals.rows.remaining + totals.tracks.remaining
    const photosRemaining = totals.photos.remaining
    const aheadInQueue = rowsRemaining > 0 ? (queueGroup?.ahead ?? 0) : 0
    const stall = this.stallState()
    const rowsStalled = stall.rows && rowsRemaining > 0
    const photosStalled = stall.photos && photosRemaining > 0

    let phase: UploadPhase
    let blockedBy: ProjectUploadSnapshot["blockedBy"]
    let stalledOnOtherProject = false
    if (rowsRemaining + photosRemaining === 0) {
      phase = this.completed.has(projectId) ? "done" : "idle"
    } else if (this.isWaitingForConnection(rowsRemaining, photosRemaining)) {
      phase = "waiting"
      blockedBy = "offline"
    } else if (rowsStalled || photosStalled) {
      phase = "stalled"
      blockedBy = "no_progress"
      stalledOnOtherProject =
        rowsStalled && !photosStalled && this.headProjectId() !== projectId
    } else if (aheadInQueue > 0 && photosRemaining === 0) {
      phase = "waiting"
    } else {
      phase = "uploading"
    }

    return {
      loaded: this.isLoaded(),
      phase,
      rows: totals.rows,
      photos: { ...totals.photos, bytesRemaining: photoGroup?.bytes ?? 0 },
      tracks: totals.tracks,
      items: totals.items,
      failures,
      otherFailures,
      aheadInQueue,
      lastSuccessAt: this.signals.lastSuccessAt,
      blockedBy,
      stalledOnOtherProject,
      isLarge: rowsRemaining + photosRemaining > 0 && isLargeSession(totals),
      completed: phase === "done" ? this.completed.get(projectId) : undefined,
    }
  }

  private computeGlobal(): GlobalUploadSnapshot {
    const all = this.totalsFor(ALL_PROJECTS)
    const deletions = all.deletions.remaining
    const visibleRows = all.rows.remaining + all.tracks.remaining
    // Deleted records' changes are hidden from the projects' status, not
    // from the upload mechanics: they hold the queue like any other change.
    const rows = visibleRows + deletions
    const photos = all.photos.remaining
    const pending = rows + photos
    const stall = this.stallState()
    // Blocked only when nothing can move: a stuck or disconnected row queue
    // doesn't stop photo files (a separate queue), and vice versa.
    const rowsMoving =
      rows > 0 && !this.signals.rowsWaitingForConnection && !stall.rows
    const photosMoving = photos > 0 && !stall.photos
    let blockedBy: GlobalUploadSnapshot["blockedBy"]
    if (pending > 0 && !this.online) {
      blockedBy = "offline"
    } else if (pending > 0 && !rowsMoving && !photosMoving) {
      blockedBy =
        rows > 0 && this.signals.rowsWaitingForConnection
          ? "offline"
          : "no_progress"
    }
    return {
      loaded: this.isLoaded(),
      rows: all.rows.remaining,
      tracks: all.tracks.remaining,
      photos: all.photos.remaining,
      items: all.items.remaining,
      deletions,
      failures: this.failures.reduce((sum, group) => sum + group.failures, 0),
      hasPending: pending > 0,
      isOnline: this.online,
      blockedBy,
      shouldKeepAwake: pending > 0 && !blockedBy && isLargeSession(all),
      stalledOnDeletion: stall.rows && deletions > 0 && visibleRows === 0,
    }
  }
}

/** The app's one monitor. */
export const uploadMonitor = new UploadMonitor()
